import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/lib/media";
import { mediaTypeLabel } from "@/lib/media";
import { useFavorites } from "@/hooks/useFavorites";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const { isMovieFavorite, toggleMovie } = useFavorites();

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  const goTo = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  if (items.length === 0) return null;

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-foreground-950 md:h-[620px] lg:h-[680px]"
      aria-label="焦点影视推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStart.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStart.current === null) return;
        const distance = event.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(distance) > 48) {
          goTo(distance > 0 ? index - 1 : index + 1);
        }
        touchStart.current = null;
      }}
    >
      {items.map((movie, slideIndex) => {
        const active = slideIndex === index;
        return (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={!active}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title}剧照`}
              title={`${movie.title} 影视推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />
          </div>
        );
      })}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] animate-float-in flex-col justify-end px-4 pb-16 md:px-8 md:pb-20">
        {items.map((movie, slideIndex) => {
          const active = slideIndex === index;
          return (
            <div
              key={movie.id}
              className={`w-full max-w-2xl transition-all duration-500 ${
                active ? "block opacity-100" : "hidden opacity-0"
              }`}
            >
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                  {slideIndex === 0 ? "本周焦点" : "编辑推荐"}
                </span>
                <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-[11px] text-background-50/85">
                  {mediaTypeLabel[movie.type]}
                </span>
                <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-[11px] text-background-50/85">
                  {movie.year} · {movie.region}
                </span>
              </div>
              {active ? (
                <h1 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                  {movie.title}
                </h1>
              ) : (
                <h2 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                  {movie.title}
                </h2>
              )}
              <div className="mt-4 flex items-center gap-3">
                <span className="font-heading text-2xl font-semibold text-accent-300">
                  {movie.score.toFixed(1)}
                </span>
                <span className="text-xs text-background-50/70">综合评分</span>
                <span className="h-3 w-px bg-background-50/30" />
                <span className="text-xs text-background-50/70">{movie.genres.join(" / ")}</span>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-50/85 md:text-base">
                {movie.summary}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${movie.id}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-play-circle-line text-base" />
                  </span>
                  查看详情
                </Link>
                <Link
                  to={`/category/${movie.type}`}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10"
                >
                  更多{mediaTypeLabel[movie.type]}
                </Link>
                <button
                  type="button"
                  onClick={() => toggleMovie(movie.id)}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm transition-colors ${
                    isMovieFavorite(movie.id)
                      ? "bg-background-50/20 text-background-50"
                      : "text-background-50/80 hover:text-background-50"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i
                      className={
                        isMovieFavorite(movie.id) ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"
                      }
                    />
                  </span>
                  {isMovieFavorite(movie.id) ? "已收藏" : "加入收藏"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute bottom-8 right-4 z-20 flex items-center gap-3 md:right-8">
        <div className="flex items-center gap-1.5">
          {items.map((movie, dotIndex) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到第 ${dotIndex + 1} 张`}
              onClick={() => goTo(dotIndex)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                dotIndex === index ? "w-7 bg-background-50" : "w-3 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一张"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一张"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}